<script setup lang="ts">
import SubCardHeading from "../SubCardHeading.vue";
import CardHeading from "../CardHeading.vue";
// Vue
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { ClipboardCheck, ClipboardList, PencilRuler, Plus, Share2, Waypoints } from "@lucide/vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";

// Types
import { useEditBuildingProjectWizard } from "@/features/BuildingProject";
import { GetBasicProjectListQuery } from "@/graphql";

// Utils
import {
  attachNewStreamToProject,
  attachSharedStreamToProject,
} from "@/features/BuildingProject/services/CreateBuildingProjectService";
import { useBasicProjectList } from "@/api/composables/useBasicProjectList.composable";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useSpeckleUser } from "@/stores/speckle/speckleUser.store";
import { openSpeckleModal } from "@features/Speckle";

// Components
import Button from "@/components/Reusables/Buttons/Button.vue";
import CheckBox from "@/components/Reusables/Inputs/CheckBox.vue";
import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";
import StepCard from "../StepCard.vue";
import LoadingWrapper from "@/components/Reusables/Loading/LoadingWrapper/LoadingWrapper.vue";
import SearchSelectProjects from "@/components/Reusables/Inputs/SearchSelectProjects.vue";
import SpeckleConnectorGuide from "./SpeckleConnectorGuide.vue";

const editBuildingProjectWizard = useEditBuildingProjectWizard();
const toast = useToast();

/**
 * Computeds over the wizard's wrapper, not refs captured at setup. This component now has a state
 * transition to survive — a project without a stream gains one from the Connect button below, and the
 * screen has to swap from the empty state to the connection cards without a remount. Refs took the
 * wrapper's values once and would keep showing the empty state after the connect succeeded.
 */
const models = computed(() => editBuildingProjectWizard.getProjectWrapper().models);
const streamId = computed(() => editBuildingProjectWizard.getProjectWrapper().project.streamId || "");
const wasCopied = ref<boolean>(false);
const url = computed(() => import.meta.env.VITE_APIMANAGER_BASEURL + "/streams/" + streamId.value);

/**
 * Whether this project has a model connection at all.
 *
 * Both absent spellings, deliberately: a project created by the start dialog carries `null` (the
 * base insert never writes a stream id), while one whose stream attachment failed carries `""`.
 * `streamId` above already folds them together. This is also why the step itself no longer hides on
 * a null stream (settingsSections.ts): the dialog's design is "a 3D model is attached to a project
 * that exists, from the project's own 3D model settings" — this screen IS those settings, so it has
 * to exist for exactly the projects that have nothing yet.
 */
const hasStream = computed(() => streamId.value.length > 0);

// Which models are active is the MODELS step's business now, regardless of a connection — this
// screen only reads the active ones back to seed a brand-new stream's initial branches.
const getActiveModels = () => {
  const activeModels = models.value.filter((model) => model.isActive);
  return activeModels;
};

const copyToClipboard = () => {
  navigator.clipboard.writeText(url.value);
  wasCopied.value = true;
};

/**
 * Account gate. Connecting needs a Speckle account linked to the user's login (the stream is created
 * and published to as that account), so without one the connect options are replaced by the link
 * step instead of a flow that fails at the end (RTLCA-5439's root cause).
 */
const speckleUserStore = useSpeckleUser();
const { isLinked, isLoadingStatus, hasFetchedStatus, hasStatusError } = storeToRefs(speckleUserStore);
const accountStatusPending = computed(() => isLoadingStatus.value && !hasFetchedStatus.value);

onMounted(() => void speckleUserStore.ensureUserStatus());

// The modal owns the account flow; refresh afterwards so the gate reads the outcome without a reload.
const linkSpeckleAccount = async () => {
  await openSpeckleModal();
  await speckleUserStore.refreshUserStatus();
};
const retryAccountStatus = () => void speckleUserStore.refreshUserStatus();

/**
 * Sharing another project's connection: the create wizard's second choice, on the settings page.
 * Only projects that hold a stream are offered, never this one.
 */
const authState = useAuthStateStore();
const { hasAccessAllProjects } = storeToRefs(authState);
const { fetchBasicProjects, basicProjects, basicProjectListLoading } = useBasicProjectList();
const sharedProject = ref<GetBasicProjectListQuery["project"][number]>();
const sharedModelSelection = ref<Record<string, boolean>>({});

const shareableProjects = computed(() => {
  const ownId = editBuildingProjectWizard.getProjectWrapper().project.id;
  return basicProjects.value
    .filter((candidate) => !!candidate.speckleStreamId && candidate.id !== ownId)
    .map((candidate) => ({ name: candidate.name, isFavorite: !!candidate.favorite }));
});

// `immediately`: the composable debounces by default for search-as-you-type; this is one mount-time load.
const stopAccessWatch = watch(
  hasAccessAllProjects,
  (newHasAccessAllProjects) => {
    if (newHasAccessAllProjects === undefined || hasStream.value) return;
    fetchBasicProjects({ hasAccessAllProjects: newHasAccessAllProjects, immediately: true });
  },
  { immediate: true }
);
onUnmounted(() => stopAccessWatch());

const handleSharedProjectChange = (projectName: string) => {
  const found = basicProjects.value.find((candidate) => candidate.name === projectName && !!candidate.speckleStreamId);
  sharedProject.value = found;
  // Every model of the shared stream included by default; leaving one out is the exception.
  sharedModelSelection.value = Object.fromEntries((found?.models ?? []).map((model) => [model.name, true]));
};

const toggleSharedModel = (modelName: string, included: boolean) => {
  sharedModelSelection.value = { ...sharedModelSelection.value, [modelName]: included };
};

const sharedModelNames = computed(() =>
  Object.entries(sharedModelSelection.value)
    .filter(([, included]) => included)
    .map(([modelName]) => modelName)
);

// Which connect is in flight, so only its button spins while both stay disabled.
const connectingVia = ref<"new" | "shared" | null>(null);

/**
 * Give this project a model connection, now.
 *
 * An immediate action rather than a field the page's Save picks up, because it is not project DATA —
 * it creates or borrows an external resource (the Speckle stream) and attaches it, the same writes the
 * create flow performs. Both attach recipes are that flow's own, exported, traps included (stream
 * first so a failure orphans a stream rather than dangling a project; a materializing read before the
 * attach).
 *
 * `rebaseOnServer` afterwards, because the attach happened outside the save flow: it force-reads the
 * server's copy and folds only the Speckle-owned fields (`streamId`, `speckleData`, `modelNames`,
 * the model rows) onto the wrapper and its save/discard baseline, so this section reads as connected
 * without disturbing an unsaved edit anywhere else on the page (RTLCA-5850).
 */
const connectWith = async (
  via: "new" | "shared",
  attach: () => Promise<string>,
  messages: { success: string; failure: string }
) => {
  const project = editBuildingProjectWizard.getProjectWrapper().project;
  connectingVia.value = via;
  try {
    const attachedStreamId = await attach();
    await editBuildingProjectWizard.rebaseOnServer(project.id);

    // Success is READ BACK, not inferred from the attach's status code. Measured on dev
    // (2026-08-28, projects 8c9fe0b0 and 4550f01d): updateSpeckleMetaData answers 200 on a
    // dialog-created project and drops the write — every call in the chain succeeds and the re-read
    // still says null. Trusting the 200 made this toast announce a stream URL over a screen that
    // kept showing the empty state. Until the API is fixed, the honest outcome of that server fault
    // is a failure the user and Sentry both see, not a success that describes nothing.
    if (streamId.value !== attachedStreamId) {
      throw new Error(
        `Stream ${attachedStreamId} was attached with a 200 but the project still reads streamId=` +
          `${JSON.stringify(streamId.value)} — the API acknowledged the attach without persisting it`
      );
    }
    toast.success(messages.success);
  } catch (error) {
    Sentry.captureException(error);
    toast.error(messages.failure);
  } finally {
    connectingVia.value = null;
  }
};

const handleConnect = () => {
  const project = editBuildingProjectWizard.getProjectWrapper().project;
  // The wrapper's models mapped into the create flow's local shape. On a model-less project this
  // list is empty and the stream is created with no branches; the Models section is where branches
  // get added afterwards, exactly as on a wizard-created project. No enumValue: these are stored
  // models, not the wizard's building-type defaults, so any that exist are created as named branches.
  const activeModels = getActiveModels().map((model) => ({ name: model.name, isActive: model.isActive ?? true }));
  return connectWith("new", () => attachNewStreamToProject(project.id, project.projectName, activeModels), {
    success: "3D model connection created. Publish your model to the Speckle Project linked below.",
    failure: "The 3D model connection could not be created. Try again.",
  });
};

const handleConnectShared = () => {
  const project = editBuildingProjectWizard.getProjectWrapper().project;
  const shared = sharedProject.value;
  const sharedStreamId = shared?.speckleStreamId;
  if (!shared || !sharedStreamId) return;
  return connectWith("shared", () => attachSharedStreamToProject(project.id, sharedStreamId, sharedModelNames.value), {
    success: `Connected to the 3D model of ${shared.name}. Its published versions now feed this project too.`,
    // Seen on dev: the API lists the shared stream's models with the user's own Speckle token, so a
    // stream the user cannot open on Speckle fails here even though the project is visible in the app.
    failure: `Could not connect to the model of ${shared.name}. You may not have access to its Speckle project.`,
  });
};
</script>

<template>
  <!-- The edit wizard's model screen, on the same card grammar as the create wizard's model step
       (finding 52 gave the create paths this shape; this is their edit-side sibling). The old
       InfoIcon tooltips became each card's caption line: the same words, readable without hovering
       anything. -->
  <div class="w-full">
    <!-- No connection yet: the fork the start dialog deferred to this page. Its create flow
         deliberately writes no stream ("a 3D model is attached to a project that exists, from the
         project's own 3D model settings"), so every dialog-created project arrives here holding
         nothing — and this used to be the one state the section did not render for at all. -->
    <div
      v-if="!hasStream"
      class="flex w-full flex-col gap-3">
      <!-- Account gate first: the connect options only make sense for a login with a Speckle account,
           so until the status is known and linked, one card carries the gate and nothing else. -->
      <StepCard
        v-if="accountStatusPending || hasStatusError || !isLinked"
        :icon="Waypoints"
        heading="Connect a 3D model"
        caption="The connection carries your 3D model into the platform through Speckle, where you map EPDs or materials onto the model's materials.">
        <div
          v-if="accountStatusPending"
          class="t-small text-type-muted flex items-center gap-2"
          role="status">
          <LoadingSpinner
            size="xs"
            fill="gray" />
          <span>Checking your Speckle account</span>
        </div>

        <template v-else-if="hasStatusError">
          <p class="t-small text-type-default max-w-prose">
            Your Speckle account status could not be checked, and connecting a model needs it.
          </p>
          <Button
            class="self-start"
            label="Retry"
            color="button"
            @click="retryAccountStatus" />
        </template>

        <template v-else>
          <p class="t-small text-type-default max-w-prose">
            Connecting needs a Speckle account linked to your login: the connection is created as that account, and it is the
            account you publish with. Create one or link an existing one, then come back here.
          </p>
          <Button
            class="self-start"
            label="Link Speckle account"
            color="primary"
            @click="linkSpeckleAccount" />
        </template>
      </StepCard>

      <!-- Two ways in, side by side: a choice reads at a glance across a row where it read as a
           sequence down a column. `items-start` so the shorter card does not stretch to the picker's
           height with nothing to fill it. -->
      <div
        v-else
        class="grid gap-3 md:grid-cols-2 md:items-start">
        <StepCard
          :icon="Plus"
          heading="New connection"
          caption="Creates a new Speckle Project. Publish your model to it from your authoring tool's Speckle connector.">
          <Button
            class="self-start"
            label="Create connection"
            color="primary"
            :is-loading="connectingVia === 'new'"
            :disabled="connectingVia !== null"
            @click="handleConnect" />
        </StepCard>

        <StepCard
          :icon="Share2"
          heading="Shared connection"
          caption="This project receives data from an existing Speckle Project. Both projects read the same published model, useful for comparing variants.">
          <LoadingWrapper :loading="basicProjectListLoading">
            <div class="flex flex-col gap-3">
              <SearchSelectProjects
                :key="sharedProject?.id"
                label="Project to share with"
                hide-label
                :options="shareableProjects"
                placeholder="Search projects with a 3D model"
                :keep-after-select="true"
                :value="sharedProject?.name"
                @option-selected="handleSharedProjectChange" />
              <p
                v-if="shareableProjects.length === 0"
                class="t-caption text-type-muted">
                None of your other projects has a 3D model connected yet.
              </p>

              <div
                v-if="sharedProject"
                class="flex flex-col gap-2">
                <span class="t-caption text-type-muted">Models to include</span>
                <ul
                  v-if="sharedProject.models.length > 0"
                  class="flex flex-wrap gap-x-6 gap-y-2">
                  <li
                    v-for="model in sharedProject.models"
                    :key="model.id">
                    <CheckBox
                      :value="sharedModelSelection[model.name] ?? false"
                      :label="model.name"
                      size="sm"
                      color="input"
                      @update:value="toggleSharedModel(model.name, $event)" />
                  </li>
                </ul>
                <p
                  v-else
                  class="t-caption text-type-muted">
                  This connection carries no models yet. Add them under Model configuration once connected.
                </p>
                <Button
                  class="self-start"
                  :label="`Connect to the model of ${sharedProject.name}`"
                  color="primary"
                  :is-loading="connectingVia === 'shared'"
                  :disabled="connectingVia !== null"
                  @click="handleConnectShared" />
              </div>
            </div>
          </LoadingWrapper>
        </StepCard>
      </div>

      <SpeckleConnectorGuide :connected="false" />

      <!-- The third door as one quiet row rather than a third card: it is the fallback, and a row says
           so where a card of equal weight made it read as a third recommendation. The link is a link,
           not a Button: it navigates. -->
      <div class="rounded-8 bg-slate-0 flex flex-wrap items-center justify-between gap-3 border border-slate-300 px-5 py-3">
        <div class="flex min-w-0 items-center gap-3">
          <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
            <PencilRuler
              class="icon-sm text-app-blue-600"
              :stroke-width="1.5"
              aria-hidden="true" />
          </span>
          <div class="flex min-w-0 flex-col">
            <CardHeading class="t-small-bold text-type-default">Or work without a model</CardHeading>
            <p class="t-caption text-type-muted">
              Enter building elements by hand, import them from Excel, or load samples. A model can be connected later.
            </p>
          </div>
        </div>
        <RouterLink
          :to="`/project/${editBuildingProjectWizard.getProjectWrapper().project.id}/mapping`"
          class="focus-ring rounded-8 t-small-bold text-type-default duration-fast ease-standard ring-slate-250 bg-slate-0 shrink-0 px-4 py-2 ring-1 transition-all hover:ring-slate-300">
          Go to Materials
        </RouterLink>
      </div>
    </div>

    <div
      v-else
      class="flex w-full flex-col gap-3">
      <!-- Speckle connection -->
      <StepCard
        :icon="Waypoints"
        heading="Speckle connection"
        caption="The Speckle connection carries your 3D model into the platform, where you map EPDs or materials onto the model's materials. When the model changes, publish it again and the emission calculation updates accordingly.">
        <SubCardHeading class="t-small-bold text-type-default"
          >Speckle Project link <span class="t-xs text-type-muted">(click to copy)</span></SubCardHeading
        >
        <button
          v-if="streamId"
          type="button"
          data-cy="copy-streamid-btn"
          aria-label="Copy Speckle Project link to clipboard"
          class="text-type-default focus-ring rounded-8 bg-slate-0 t-small-bold duration-fast ease-standard flex cursor-pointer items-center justify-center gap-4 self-start border border-slate-300 px-5 py-3 transition-colors hover:bg-slate-50"
          @click="copyToClipboard">
          <span>{{ url }}</span>
          <ClipboardList
            v-if="!wasCopied"
            class="icon-sm shrink-0"
            :stroke-width="1.5"
            aria-hidden="true" />
          <ClipboardCheck
            v-else
            class="icon-sm text-app-green-500 shrink-0"
            :stroke-width="1.5"
            aria-hidden="true" />
        </button>
      </StepCard>

      <!-- Collapsed once a stream exists: with the URL above, the guide is look-up material, not the next step. -->
      <SpeckleConnectorGuide
        :connected="true"
        :project-id="editBuildingProjectWizard.getProjectWrapper().project.id"
        collapsible />
    </div>
  </div>
</template>
